import * as React from 'react';
import {
  applyServerErrors,
  BottomSheet,
  BottomSheetContent,
  Button,
  Form,
  FormField,
  Input,
  localToday,
  newUserSchema,
  NO_OFFICE_ROLE,
  OptionSheet,
  PhoneInput,
  startDateToIso,
  toast,
  useZodForm,
  type NewUserValues,
} from '@relay-towing/ui';
import { useCreateDriver } from '../../api/hooks/useCreateDriver';
import { useGetTrucks } from '../../api/hooks/useGetTrucks';

const NO_TRUCK = '';

const STATUSES = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'ON_LEAVE', label: 'On leave' },
];

/**
 * Add a driver from the phone's Drivers screen: the dashboard's Drivers
 * page's Add Driver, on the same schema (`newUserSchema`, someone with no
 * office role who drives) and the same `POST /drivers`. No Figma frame
 * exists for it; it is laid out as the phone's other forms are. The server
 * emails the new driver an invitation to set up their account, and holds
 * the plan's driver limit; its refusal is shown on the form.
 */
export function AddDriverSheet({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const formId = React.useId();
  const create = useCreateDriver();
  return (
    <BottomSheet open={open} onOpenChange={onOpenChange}>
      <BottomSheetContent
        title="Add driver"
        size="full"
        footer={
          <>
            <Button
              variant="secondary"
              size="xl"
              pill
              disabled={create.isPending}
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form={formId}
              size="xl"
              pill
              loading={create.isPending}
            >
              Add driver
            </Button>
          </>
        }
      >
        <AddDriverForm
          id={formId}
          onAdd={async (values) => {
            await create.mutateAsync({
              name: values.name.trim(),
              email: values.email.trim(),
              phone: values.phone || undefined,
              assignedTruckId: values.truckId || undefined,
              status: values.status,
              startDate: startDateToIso(values.startDate ?? localToday()),
            });
            onOpenChange(false);
            toast.success('Driver added', {
              description: 'They get an email to set up their account.',
            });
          }}
        />
      </BottomSheetContent>
    </BottomSheet>
  );
}

/** The fields, made fresh each time the sheet opens. */
function AddDriverForm({
  id,
  onAdd,
}: {
  id: string;
  onAdd: (values: NewUserValues) => Promise<void>;
}) {
  const { data: trucks } = useGetTrucks();
  const form = useZodForm(newUserSchema, {
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      // Someone who only drives, as the dashboard's Add Driver starts.
      officeRole: NO_OFFICE_ROLE,
      drives: true,
      truckId: NO_TRUCK,
      status: 'ACTIVE',
      startDate: localToday(),
    },
  });
  const add = async (values: NewUserValues) => {
    try {
      await onAdd(values);
    } catch (error) {
      // The server's own words: the plan's driver limit, an email in use.
      applyServerErrors(form, error);
    }
  };
  const truckOptions = [
    { value: NO_TRUCK, label: 'No truck' },
    ...(trucks?.data ?? []).map((truck) => ({
      value: truck.id,
      label: `Truck ${truck.truckNumber}`,
    })),
  ];
  return (
    <Form form={form} id={id} gap={4} onSubmit={add}>
      <FormField form={form} name="name" label="Name">
        {(field) => <Input {...field} autoComplete="off" />}
      </FormField>
      <FormField form={form} name="email" label="Email">
        {(field) => (
          <Input
            {...field}
            type="email"
            inputMode="email"
            autoCapitalize="none"
            autoComplete="off"
          />
        )}
      </FormField>
      <FormField form={form} name="phone" label="Phone">
        {(field) => <PhoneInput {...field} />}
      </FormField>
      <FormField form={form} name="truckId" label="Truck">
        {(field) => (
          <OptionSheet
            title="Truck"
            options={truckOptions}
            value={field.value ?? NO_TRUCK}
            onValueChange={field.onChange}
          />
        )}
      </FormField>
      <FormField form={form} name="status" label="Status">
        {(field) => (
          <OptionSheet
            title="Status"
            options={STATUSES}
            value={field.value}
            onValueChange={field.onChange}
          />
        )}
      </FormField>
      <FormField form={form} name="startDate" label="Start date">
        {(field) => <Input {...field} type="date" max={localToday()} />}
      </FormField>
    </Form>
  );
}
